// Lightweight client-side auth helper: stores the token in localStorage,
// exposes login/register/logout, and a global fetch interceptor that adds the
// Authorization header to every API request and auto-logs-out on 401.

export const API_URL = window.location.origin;
const TOKEN_KEY = "sx-auth-token";
const USER_KEY = "sx-auth-user";
const VIEW_AS_KEY = "sx-view-as";

// --- Admin "view data as user" helpers ---------------------------------------
// When an admin picks a user from the Users page, we remember that choice here.
// Every API request then sends an x-view-as-user header so the backend returns
// that user's data through the normal pages. Normal users never have this set.

export function getViewAs() {
  try {
    const raw = localStorage.getItem(VIEW_AS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

// Clear all cached page data so switching users never shows stale data.
export function clearDataCaches() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.indexOf("sx_cache_") === 0) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch (err) {
    /* ignore */
  }
}

export function setViewAs(user) {
  try {
    if (user && user.id) {
      localStorage.setItem(VIEW_AS_KEY, JSON.stringify({ id: user.id, name: user.name || "", email: user.email || "" }));
    } else {
      localStorage.removeItem(VIEW_AS_KEY);
    }
  } catch (err) {
    /* ignore */
  }
  clearDataCaches();
}

export function clearViewAs() {
  try {
    localStorage.removeItem(VIEW_AS_KEY);
  } catch (err) {
    /* ignore */
  }
  clearDataCaches();
}

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch (err) {
    return "";
  }
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

export function setSession(token, user) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (err) {
    /* ignore */
  }
}

export function clearSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (err) {
    /* ignore */
  }
}

export function isLoggedIn() {
  return Boolean(getToken());
}

async function postJson(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {})
  });
  let data = {};
  try {
    data = await res.json();
  } catch (err) {
    data = {};
  }
  if (!res.ok) {
    throw new Error(data.message || "Something went wrong. Please try again.");
  }
  return data;
}

export async function login(email, password) {
  const data = await postJson("/api/auth/login", { email, password });
  // Wipe any previous user's cached page data before starting this session so
  // the new user never sees someone else's data flash on screen.
  clearDataCaches();
  clearViewAs();
  setSession(data.token, data.user);
  return data;
}

export async function register(name, email, password) {
  const data = await postJson("/api/auth/register", { name, email, password });
  // Fresh account: start from a completely clean cache so all pages show empty
  // states until the user adds their own data.
  clearDataCaches();
  clearViewAs();
  setSession(data.token, data.user);
  return data;
}

export async function forgotPassword(email) {
  return postJson("/api/auth/forgot-password", { email });
}

export async function resetPassword(token, password) {
  return postJson("/api/auth/reset-password", { token, password });
}

export async function verifyEmail(token) {
  return postJson("/api/auth/verify-email", { token });
}

export function logout() {
  clearSession();
  clearViewAs();
  window.location.hash = "";
  window.location.reload();
}

// Install a one-time global fetch interceptor. Adds the bearer token to every
// same-app API call and clears the session on a 401 so the login screen shows.
let installed = false;
export function installAuthFetchInterceptor() {
  if (installed || typeof window === "undefined" || !window.fetch) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    let url = "";
    try {
      url = typeof input === "string" ? input : input && input.url ? input.url : "";
    } catch (err) {
      url = "";
    }
    const isApiCall = url.includes("/api/");
    const isAuthCall = url.includes("/api/auth/");
    const token = getToken();
    let nextInit = init || {};
    if (isApiCall && !isAuthCall && token) {
      const headers = new Headers(nextInit.headers || (typeof input !== "string" && input.headers) || {});
      if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
      const viewAs = getViewAs();
      if (viewAs && viewAs.id && !headers.has("x-view-as-user")) headers.set("x-view-as-user", String(viewAs.id));
      nextInit = { ...nextInit, headers };
    }
    const response = await originalFetch(input, nextInit);
    if (response.status === 401 && isApiCall && !isAuthCall) {
      clearSession();
      if (!window.__sxAuthReloading) {
        window.__sxAuthReloading = true;
        window.location.reload();
      }
    }
    return response;
  };
}
